import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import type { UploadProgressBarStatus } from '@/constants/upload-workflow';
import { UPLOAD_STATUS, WorkflowError, WorkflowErrorCode, WorkflowState } from '@/constants/upload-workflow';
import { useSentry } from '@/hooks/useSentry';
import {
  useIsUploading,
  useSetIsUploading,
  useSetWorkflowError,
  useSetWorkflowState,
  useUploadClearSessionFiles,
  useUploadStateStore,
  useUploadUpdateAsset,
  useUploadUpdateSessionStatus,
  useWorkflowError,
  useWorkflowState,
} from '@/store/useUploadStateStore';
import { getResumeStartByte, UploadError, UploadErrorType, uploadFile } from '@/utils/asset-upload';
import { invalidateContentCaches } from '@/utils/cms/invalidate-content-caches';
import { AssetType } from '@/utils/constants/uploads';

type WorkflowReturn = {
  canPause: boolean;
  canUpload: () => boolean;
  error: WorkflowError | null;
  pauseUpload: () => void;
  /** Upload toast MiniBar only: `processing` is never set here (CMS-only on the detail page). */
  progressBarStatus: Exclude<UploadProgressBarStatus, 'processing'>;
  state: WorkflowState;
  uploadFiles: () => void;
  /** Bytes-uploaded progress from 0 to 1. When no session, 0. */
  uploadProgress: number;
};

/**
 * React hook for managing the GCS upload of an existing session, whatever content type created it.
 * The upload form creates the session with its files and sets status to 'uploading'; this hook
 * picks up from there and runs the transfer, with resume, pause and network auto-pause.
 *
 * @param operationName - The operation name identifying the upload session.
 * @returns Workflow state and control functions.
 * @example
 * const workflow = useCmsUploadWorkflow('operation-123');
 * if (workflow.canPause) {
 *   workflow.pauseUpload();
 * }
 */
export function useCmsUploadWorkflow(operationName: string): WorkflowReturn {
  const updateAsset = useUploadUpdateAsset();
  const updateSessionStatus = useUploadUpdateSessionStatus();
  const clearSessionFiles = useUploadClearSessionFiles();
  const setWorkflowState = useSetWorkflowState();
  const setWorkflowError = useSetWorkflowError();
  const setIsUploading = useSetIsUploading();
  const { captureException } = useSentry();
  const queryClient = useQueryClient();

  const state = useWorkflowState(operationName);
  const error = useWorkflowError(operationName);
  const isUploading = useIsUploading(operationName);

  const isPausedRef = useRef(false);

  const session = useUploadStateStore(s => s.sessions[operationName]);

  const uploadProgress = useMemo(() => {
    const s = session;
    if (!s) {
      return 0;
    }
    const v = s.videoUpload;
    const t = s.imageUpload;
    const videoTotal = v.file?.size ?? v.fileMetadata?.size ?? 0;
    const imageTotal = t.file?.size ?? t.fileMetadata?.size ?? 0;
    const total = videoTotal + imageTotal;
    if (total === 0) {
      return 0;
    }
    const videoDone =
      v.status === UPLOAD_STATUS.completed ? videoTotal : Math.min(Math.max(0, v.lastUploadedByte + 1), videoTotal);
    const imageDone =
      t.status === UPLOAD_STATUS.completed ? imageTotal : Math.min(Math.max(0, t.lastUploadedByte + 1), imageTotal);
    return (videoDone + imageDone) / total;
  }, [session]);

  const progressBarStatus = useMemo((): Exclude<UploadProgressBarStatus, 'processing'> => {
    if (session?.status === UPLOAD_STATUS.paused) {
      return UPLOAD_STATUS.paused;
    }
    return UPLOAD_STATUS.uploading;
  }, [session?.status]);

  useEffect(() => {
    isPausedRef.current = session?.status === UPLOAD_STATUS.paused;
  }, [session?.status]);

  const markUploadSuccess = useCallback(() => {
    const kind = useUploadStateStore.getState().sessions[operationName]?.kind;
    updateAsset(operationName, AssetType.Video, { status: UPLOAD_STATUS.completed });
    updateAsset(operationName, AssetType.Image, { status: UPLOAD_STATUS.completed });
    updateSessionStatus(operationName, UPLOAD_STATUS.completed);
    clearSessionFiles(operationName);
    setWorkflowState(operationName, 'success');
    void invalidateContentCaches(queryClient, kind);
  }, [operationName, updateAsset, updateSessionStatus, clearSessionFiles, setWorkflowState, queryClient]);

  const performUpload = useCallback(async () => {
    const store = useUploadStateStore.getState();
    const currentSession = store.sessions[operationName];
    if (!currentSession) {
      return;
    }

    const videoUpload = currentSession.videoUpload;
    const imageUpload = currentSession.imageUpload;

    const videoNeedsUpload = videoUpload.status !== UPLOAD_STATUS.completed;
    const imageNeedsUpload = imageUpload.status !== UPLOAD_STATUS.completed;

    // Validate we have what we need
    if (videoNeedsUpload && (!videoUpload.sessionUrl || !videoUpload.file)) {
      setWorkflowError(operationName, { code: WorkflowErrorCode.SESSION_EXPIRED, step: 'upload' });
      setWorkflowState(operationName, 'error');
      updateSessionStatus(operationName, UPLOAD_STATUS.failed);
      return;
    }

    if (imageNeedsUpload && (!imageUpload.sessionUrl || !imageUpload.file)) {
      setWorkflowError(operationName, { code: WorkflowErrorCode.SESSION_EXPIRED, step: 'upload' });
      setWorkflowState(operationName, 'error');
      updateSessionStatus(operationName, UPLOAD_STATUS.failed);
      return;
    }

    // If neither needs upload, all assets are already done
    if (!videoNeedsUpload && !imageNeedsUpload) {
      markUploadSuccess();
      return;
    }

    if (!useUploadStateStore.getState().tryAcquireContentUpload(operationName)) {
      return;
    }

    setWorkflowState(operationName, 'upload');
    setWorkflowError(operationName, null);

    try {
      const uploadPromises: Array<Promise<void>> = [];

      // Upload video if needed
      if (videoNeedsUpload && videoUpload.sessionUrl && videoUpload.file) {
        const fileSize = videoUpload.file.size;
        let videoStartByte: number;
        try {
          const result = await getResumeStartByte(videoUpload.sessionUrl, fileSize, videoUpload.lastUploadedByte);
          if (result.lastPersistedByte > videoUpload.lastUploadedByte) {
            updateAsset(operationName, AssetType.Video, { lastUploadedByte: result.lastPersistedByte });
          }
          videoStartByte = result.startByte;
        } catch (err) {
          if (err instanceof UploadError && err.errorType === UploadErrorType.SessionExpired) {
            captureException(err, {
              tags: {
                component: 'useCmsUploadWorkflow',
                step: 'upload',
                errorType: 'SessionExpired',
                assetType: 'video',
              },
              extras: {
                operationName,
                fileSize,
                lastUploadedByte: videoUpload.lastUploadedByte,
              },
            });
            setWorkflowError(operationName, { code: WorkflowErrorCode.SESSION_EXPIRED, step: 'upload' });
            setWorkflowState(operationName, 'error');
            updateSessionStatus(operationName, UPLOAD_STATUS.failed);
            setIsUploading(operationName, false);
            return;
          }
          // Non-SessionExpired errors: log and fall back to last known byte
          captureException(err, {
            tags: {
              component: 'useCmsUploadWorkflow',
              step: 'upload',
              errorType: 'ResumeStartByteFailed',
              assetType: 'video',
            },
            extras: {
              operationName,
              fileSize,
              lastUploadedByte: videoUpload.lastUploadedByte,
            },
            level: 'warning',
          });
          videoStartByte = videoUpload.lastUploadedByte + 1;
        }

        if (videoStartByte < fileSize) {
          updateAsset(operationName, AssetType.Video, { status: UPLOAD_STATUS.uploading });

          uploadPromises.push(
            uploadFile(videoUpload.sessionUrl, videoUpload.file, {
              adaptiveChunkSize: true,
              startFromByte: videoStartByte,
              onChunkComplete: (lastUploadedByte: number) => {
                updateAsset(operationName, AssetType.Video, { lastUploadedByte });

                if (lastUploadedByte + 1 === fileSize) {
                  updateAsset(operationName, AssetType.Video, { status: UPLOAD_STATUS.completed });
                }
              },
              shouldStop: () => isPausedRef.current,
            })
          );
        } else {
          // All bytes already uploaded but status wasn't updated - mark as completed
          updateAsset(operationName, AssetType.Video, { status: UPLOAD_STATUS.completed });
        }
      }

      // Upload the image if needed
      if (imageNeedsUpload && imageUpload.sessionUrl && imageUpload.file) {
        const imageFileSize = imageUpload.file.size;
        let imageStartByte: number;
        try {
          const result = await getResumeStartByte(imageUpload.sessionUrl, imageFileSize, imageUpload.lastUploadedByte);
          if (result.lastPersistedByte > imageUpload.lastUploadedByte) {
            updateAsset(operationName, AssetType.Image, { lastUploadedByte: result.lastPersistedByte });
          }
          imageStartByte = result.startByte;
        } catch (err) {
          if (err instanceof UploadError && err.errorType === UploadErrorType.SessionExpired) {
            captureException(err, {
              tags: {
                component: 'useCmsUploadWorkflow',
                step: 'upload',
                errorType: 'SessionExpired',
                assetType: 'image',
              },
              extras: {
                operationName,
                fileSize: imageFileSize,
                lastUploadedByte: imageUpload.lastUploadedByte,
              },
            });
            setWorkflowError(operationName, { code: WorkflowErrorCode.SESSION_EXPIRED, step: 'upload' });
            setWorkflowState(operationName, 'error');
            updateSessionStatus(operationName, UPLOAD_STATUS.failed);
            setIsUploading(operationName, false);
            return;
          }
          // Non-SessionExpired errors: log and fall back to last known byte
          captureException(err, {
            tags: {
              component: 'useCmsUploadWorkflow',
              step: 'upload',
              errorType: 'ResumeStartByteFailed',
              assetType: 'image',
            },
            extras: {
              operationName,
              fileSize: imageFileSize,
              lastUploadedByte: imageUpload.lastUploadedByte,
            },
            level: 'warning',
          });
          imageStartByte = imageUpload.lastUploadedByte + 1;
        }

        if (imageStartByte < imageFileSize) {
          updateAsset(operationName, AssetType.Image, { status: UPLOAD_STATUS.uploading });

          uploadPromises.push(
            uploadFile(imageUpload.sessionUrl, imageUpload.file, {
              adaptiveChunkSize: true,
              startFromByte: imageStartByte,
              onChunkComplete: (lastUploadedByte: number) => {
                updateAsset(operationName, AssetType.Image, { lastUploadedByte });

                if (lastUploadedByte + 1 === imageFileSize) {
                  updateAsset(operationName, AssetType.Image, { status: UPLOAD_STATUS.completed });
                }
              },
              shouldStop: () => isPausedRef.current,
            })
          );
        } else {
          // All bytes already uploaded but status wasn't updated - mark as completed
          updateAsset(operationName, AssetType.Image, { status: UPLOAD_STATUS.completed });
        }
      }

      const settled = await Promise.allSettled(uploadPromises);
      const rejected = settled.filter((r): r is PromiseRejectedResult => r.status === 'rejected');

      if (rejected.length > 0) {
        const networkError = rejected.find(
          r => r.reason instanceof UploadError && r.reason.errorType === UploadErrorType.NetworkError
        );
        const err = networkError ? networkError.reason : rejected[0].reason;
        if (err instanceof UploadError && err.errorType === UploadErrorType.NetworkError) {
          captureException(err, {
            tags: {
              component: 'useCmsUploadWorkflow',
              step: 'upload',
              errorType: 'NetworkError',
            },
            extras: { operationName },
            level: 'warning',
          });
          isPausedRef.current = true;
          updateSessionStatus(operationName, UPLOAD_STATUS.paused);
          setWorkflowState(operationName, 'idle');
          setIsUploading(operationName, false);
          return;
        }

        const sessionAfterUpload = useUploadStateStore.getState().sessions[operationName];
        captureException(err, {
          tags: {
            component: 'useCmsUploadWorkflow',
            step: 'upload',
            errorType: 'UploadError',
          },
          extras: {
            operationName,
            videoStatus: sessionAfterUpload?.videoUpload.status,
            imageStatus: sessionAfterUpload?.imageUpload.status,
            videoLastUploadedByte: sessionAfterUpload?.videoUpload.lastUploadedByte,
            imageLastUploadedByte: sessionAfterUpload?.imageUpload.lastUploadedByte,
          },
        });
        setWorkflowError(operationName, { code: WorkflowErrorCode.UPLOAD_ERROR, step: 'upload' });
        setWorkflowState(operationName, 'error');
        setIsUploading(operationName, false);
        if (sessionAfterUpload?.videoUpload.status !== UPLOAD_STATUS.completed) {
          updateAsset(operationName, AssetType.Video, { status: UPLOAD_STATUS.failed });
        }
        if (sessionAfterUpload?.imageUpload.status !== UPLOAD_STATUS.completed) {
          updateAsset(operationName, AssetType.Image, { status: UPLOAD_STATUS.failed });
        }
        updateSessionStatus(operationName, UPLOAD_STATUS.failed);
        return;
      }

      setIsUploading(operationName, false);

      // Only respect pause if there's actual work remaining.
      // If all uploads finished, mark success regardless of pause status.
      if (isPausedRef.current) {
        // Check if all assets are completed (handles edge case where pause happens
        // during last chunk but the chunk completes anyway)
        const updatedSession = useUploadStateStore.getState().sessions[operationName];
        const allAssetsCompleted =
          updatedSession?.videoUpload.status === UPLOAD_STATUS.completed &&
          updatedSession?.imageUpload.status === UPLOAD_STATUS.completed;

        if (!allAssetsCompleted) {
          return;
        }
      }

      markUploadSuccess();
    } catch (err) {
      setIsUploading(operationName, false);

      // Auto-pause on network error - allows resume when connectivity returns
      if (err instanceof UploadError && err.errorType === UploadErrorType.NetworkError) {
        captureException(err, {
          tags: {
            component: 'useCmsUploadWorkflow',
            step: 'upload',
            errorType: 'NetworkError',
          },
          extras: {
            operationName,
          },
          level: 'warning',
        });
        isPausedRef.current = true;
        updateSessionStatus(operationName, UPLOAD_STATUS.paused);
        setWorkflowState(operationName, 'idle');
        return;
      }

      // Other errors: capture and mark as failed
      const latestSession = useUploadStateStore.getState().sessions[operationName];
      captureException(err, {
        tags: {
          component: 'useCmsUploadWorkflow',
          step: 'upload',
          errorType: 'UploadError',
        },
        extras: {
          operationName,
          videoStatus: latestSession?.videoUpload.status,
          imageStatus: latestSession?.imageUpload.status,
          videoLastUploadedByte: latestSession?.videoUpload.lastUploadedByte,
          imageLastUploadedByte: latestSession?.imageUpload.lastUploadedByte,
        },
      });

      setWorkflowError(operationName, {
        code: WorkflowErrorCode.UPLOAD_ERROR,
        step: 'upload',
      });
      setWorkflowState(operationName, 'error');

      if (!latestSession) {
        return;
      }

      const videoNeedsUpload = latestSession.videoUpload.status !== UPLOAD_STATUS.completed;
      const imageNeedsUpload = latestSession.imageUpload.status !== UPLOAD_STATUS.completed;

      if (videoNeedsUpload) {
        updateAsset(operationName, AssetType.Video, { status: UPLOAD_STATUS.failed });
      }

      if (imageNeedsUpload) {
        updateAsset(operationName, AssetType.Image, { status: UPLOAD_STATUS.failed });
      }

      updateSessionStatus(operationName, UPLOAD_STATUS.failed);
    }
  }, [
    operationName,
    updateAsset,
    updateSessionStatus,
    markUploadSuccess,
    captureException,
    setWorkflowState,
    setWorkflowError,
    setIsUploading,
  ]);

  // Auto-start upload when session status is 'uploading' and we're idle
  // Don't start if assets are already being uploaded (prevents duplicate uploads after navigation)
  useEffect(() => {
    const assetsAlreadyUploading =
      session?.videoUpload.status === UPLOAD_STATUS.uploading ||
      session?.imageUpload.status === UPLOAD_STATUS.uploading;

    if (session?.status === UPLOAD_STATUS.uploading && state === 'idle' && !isUploading && !assetsAlreadyUploading) {
      performUpload();
    }
  }, [session?.status, session?.videoUpload.status, session?.imageUpload.status, state, isUploading, performUpload]);

  const canUpload = useCallback((): boolean => {
    const currentSession = useUploadStateStore.getState().sessions[operationName];

    if (!currentSession) {
      return false;
    }

    const notCompletedOrFailed =
      currentSession.status !== UPLOAD_STATUS.completed && currentSession.status !== UPLOAD_STATUS.failed;
    const isVideoFileAvailable =
      currentSession.videoUpload.status !== UPLOAD_STATUS.completed &&
      currentSession.videoUpload.status !== UPLOAD_STATUS.failed &&
      !!currentSession.videoUpload.file;
    const isImageFileAvailable =
      currentSession.imageUpload.status !== UPLOAD_STATUS.completed &&
      currentSession.imageUpload.status !== UPLOAD_STATUS.failed &&
      !!currentSession.imageUpload.file;

    return notCompletedOrFailed && (isVideoFileAvailable || isImageFileAvailable);
  }, [operationName]);

  const uploadFiles = useCallback(() => {
    isPausedRef.current = false;
    updateSessionStatus(operationName, UPLOAD_STATUS.uploading);
    performUpload();
  }, [operationName, updateSessionStatus, performUpload]);

  const pauseUpload = useCallback(() => {
    isPausedRef.current = true;
    updateSessionStatus(operationName, UPLOAD_STATUS.paused);
  }, [operationName, updateSessionStatus]);

  return {
    canPause: state === 'upload',
    canUpload,
    error,
    pauseUpload,
    progressBarStatus,
    state,
    uploadFiles,
    uploadProgress,
  };
}
